import { useRegisterCompanyMutation, useLoginJWTMutation } from 'services/users';
import { Input } from 'ui/atoms/inputs';
import { Button } from 'ui/atoms/buttons';
import { Heading, Subheading } from 'ui/typography/Heading'
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const RegisterPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
`;

export const RegisterForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 300px;
`;

export const StyledInput = styled(Input)`
  margin-bottom: 12px;
`;

export const ErrorMessage = styled.p`
  color: #d32f2f;
  font-size: 14px;
  margin-top: 8px;
`;

export const RegisterCompanyPage = () => {
  const [registerCompany, { isLoading, isError, isSuccess }] = useRegisterCompanyMutation();
  const [ login, { isLoading: isLoginLoading, isError: isLoginError, isSuccess: isLoginSuccess } ] = useLoginJWTMutation();
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);
    const errors: Record<string, string> = {};

    const username = formData.get('username') as string;
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const confirmPassword = formData.get('confirmPassword') as string;
    const realm_name = formData.get('realm_name') as string;

    if (!username) errors.username = t('usernameRequired');
    if (!email) errors.email = t('emailRequired');
    if (!password) errors.password = t('passwordRequired');
    if (!confirmPassword) errors.confirmPassword = t('confirmPasswordRequired');
    if (!realm_name) errors.realm_name = t('realmNameRequired');
    
    if (password && confirmPassword && password !== confirmPassword) {
      errors.passwordMismatch = t('passwordMismatchAlert');
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) return;

    const companyData = {
      username,
      email,
      password,
      realm_name,
      realm_email: formData.get('realm_email') as string,
    };
    await registerCompany(companyData);
  };

  useEffect(() => {
    document.title = t('pageTitle');
  }, [t]);

  useEffect(() => {
    if (isSuccess) {
      login({ username: (document.getElementsByName('username')[0] as HTMLInputElement).value, password: (document.getElementsByName('password')[0] as HTMLInputElement).value });
    }
    if (isLoginSuccess) {
      navigate('/select-realm/');
    }
  }, [isSuccess, isLoginSuccess, navigate]);

  return (
    <RegisterPageContainer>
      <Heading>
        {t('registrationPageTitle')}
      </Heading>
      <Subheading>
        {t('subtitle')}
      </Subheading>
      <RegisterForm onSubmit={handleSubmit}>
        <StyledInput name="username" type="text" placeholder={t('usernamePlaceholder')} required />
        {validationErrors.username && <ErrorMessage>{validationErrors.username}</ErrorMessage>}
        
        <StyledInput name="email" type="email" placeholder={t('emailPlaceholder')} required />
        {validationErrors.email && <ErrorMessage>{validationErrors.email}</ErrorMessage>}
        
        <StyledInput name="password" type="password" placeholder={t('passwordPlaceholder')} required />
        {validationErrors.password && <ErrorMessage>{validationErrors.password}</ErrorMessage>}
        
        <StyledInput name="confirmPassword" type="password" placeholder={t('confirmPasswordPlaceholder')} required />
        {validationErrors.confirmPassword && <ErrorMessage>{validationErrors.confirmPassword}</ErrorMessage>}
        
        <StyledInput name="realm_name" type="text" placeholder={t('realmNamePlaceholder')} required />
        {validationErrors.realm_name && <ErrorMessage>{validationErrors.realm_name}</ErrorMessage>}
        
        <StyledInput name="realm_email" type="email" placeholder={t('realmEmailPlaceholder')} />
        
        {validationErrors.passwordMismatch && <ErrorMessage>{validationErrors.passwordMismatch}</ErrorMessage>}
        
        <Button type="submit" disabled={isLoading}>
          {isLoading ? t('loading') : t('submitButton')}
        </Button>
        {isError && <ErrorMessage>{t('errorMessage')}</ErrorMessage>}
        {isSuccess && <p style={{ color: '#4caf50' }}>{t('successMessage')}</p>}
      </RegisterForm>
    </RegisterPageContainer>
  );
}
